import React from 'react';
import { ArrowSquareOut, PlayCircle } from '@phosphor-icons/react';
import { fixMojibake } from '../../../../../../utils/text/fix-mojibake';
import { SocialMediaButtons } from '../../components/SocialMediaButtons';
import { Msg } from '../../../../../../i18n-review/client/Msg';
import { useDocumentDarkClass } from '../../../../../../hooks/useDocumentDarkClass';
import { brandAccentOnDark } from '../../../../../../utils/brand-color/adjust-brand-color';

export interface EditorialCompanyExtrasProps {
  employerOverview?: string;
  companyCoreValues?: string[];
  companyAwardsAndCertifications?: string[];
  companyVideo?: string;
  officeImages?: string[];
  companyId?: string | null;
  jobLocale: string;
  linkedinUrl?: string;
  facebookUrl?: string;
  instagramUrl?: string;
  youtubeUrl?: string;
  tiktokUrl?: string;
  brandColor?: string;
}

const dossierKickerClass = 'text-[10.5px] font-extrabold uppercase tracking-wide text-[#8C8474] border-b border-[rgba(22,21,15,0.08)] dark:border-[#4A3520]/70 pb-1.5 mb-2.5';

/**
 * TEC-512 editorial-rail redesign: surfaces a cluster of company-record fields the JDP has never
 * shown anywhere (control layout included) — employer overview, core values, awards/
 * certifications, office photos, a company video link, and social links (the individual
 * linkedinUrl/facebookUrl/etc. fields; the also-present generic `socialMediaLinks` string array
 * is a separate, unstructured field with no per-platform icon mapping, so it's not used here).
 * Every field is independently gated on its own presence, so this renders nothing at all today
 * for a job with none of them populated, and starts showing sections automatically the day any
 * of this data exists without a code change — see the TEC-512 job-data audit this responds to.
 */
export function EditorialCompanyExtras({
  employerOverview,
  companyCoreValues,
  companyAwardsAndCertifications,
  companyVideo,
  officeImages,
  companyId,
  jobLocale,
  linkedinUrl,
  facebookUrl,
  instagramUrl,
  youtubeUrl,
  tiktokUrl,
  brandColor,
}: EditorialCompanyExtrasProps): React.ReactElement | null {
  const hasOverview = Boolean(employerOverview && employerOverview.trim());
  const hasCoreValues = Boolean(companyCoreValues && companyCoreValues.length > 0);
  const hasAwards = Boolean(companyAwardsAndCertifications && companyAwardsAndCertifications.length > 0);
  const hasVideo = Boolean(companyVideo && companyVideo.trim());
  const hasImages = Boolean(officeImages && officeImages.length > 0);
  const hasSocial = Boolean(linkedinUrl || facebookUrl || instagramUrl || youtubeUrl || tiktokUrl);

  const isDark = useDocumentDarkClass();
  // brandColor is the brand panel colour (a background colour, near-black for every
  // neutral-fallback brand) and disappears as icon/text colour on the dark card; swap to the
  // dark-surface-safe accent there, matching EditorialHeadlineFacts.tsx.
  const accentColor = brandColor ? (isDark ? brandAccentOnDark(brandColor) : brandColor) : undefined;

  if (!hasOverview && !hasCoreValues && !hasAwards && !hasVideo && !hasImages && !hasSocial) return null;

  return (
    <div className="flex flex-col gap-4">
      {hasOverview ? (
        <div>
          <div className={dossierKickerClass}>
            <Msg id="jobDetailPageV2.employerOverview" defaultMessage="About the employer" description="Heading for the employer overview paragraph, editorial-rail redesign" />
          </div>
          <p className="m-0 text-[12.5px] leading-relaxed text-[#4A4438] dark:text-[#C4A882] whitespace-pre-line">
            {fixMojibake(employerOverview!)}
          </p>
        </div>
      ) : null}

      {hasCoreValues ? (
        <div>
          <div className={dossierKickerClass}>
            <Msg id="jobDetailPageV2.coreValues" defaultMessage="Core values" description="Heading for the company's core values list, editorial-rail redesign" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {companyCoreValues!.map((value, index) => (
              <span
                key={`${value}-${index}`}
                className="inline-flex items-center px-2 py-1 rounded-md bg-[#F0EDE5] dark:bg-[#2A1D0F] text-[#4A4438] dark:text-[#C4A882] text-[11px] font-semibold"
              >
                {fixMojibake(value)}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {hasAwards ? (
        <div>
          <div className={dossierKickerClass}>
            <Msg id="jobDetailPageV2.awardsAndCertifications" defaultMessage="Awards & certifications" description="Heading for the company's awards and certifications list, editorial-rail redesign" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {companyAwardsAndCertifications!.map((award, index) => (
              <span
                key={`${award}-${index}`}
                className="inline-flex items-center px-2 py-1 rounded-md bg-[#F5EDD0] dark:bg-[#2A1E08] text-[#6E4F00] dark:text-[#D4A820] text-[11px] font-semibold"
              >
                {fixMojibake(award)}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {hasImages ? (
        <div>
          <div className={dossierKickerClass}>
            <Msg id="jobDetailPageV2.officePhotos" defaultMessage="Office photos" description="Heading for the company's office photo gallery, editorial-rail redesign" />
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {officeImages!.slice(0, 6).map((src, index) => (
              <img
                key={`${src}-${index}`}
                src={src}
                alt=""
                loading="lazy"
                className="w-full aspect-square object-cover rounded-lg border border-[rgba(22,21,15,0.08)] dark:border-[#4A3520]/70"
              />
            ))}
          </div>
        </div>
      ) : null}

      {hasVideo || hasSocial ? (
        <div className="flex flex-wrap items-center gap-3">
          {hasVideo ? (
            <a
              href={companyVideo}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-1.5 text-[12px] font-bold hover:underline underline-offset-2 ${accentColor ? '' : 'text-jc-accent'}`}
              style={accentColor ? { color: accentColor } : undefined}
            >
              <PlayCircle className="h-4 w-4 flex-none" weight="fill" />
              <Msg id="jobDetailPageV2.watchCompanyVideo" defaultMessage="Watch company video" description="Link to the company's video, editorial-rail redesign" />
              <ArrowSquareOut className="h-3 w-3 flex-none" />
            </a>
          ) : null}
          {hasSocial ? (
            <SocialMediaButtons
              linkedinUrl={linkedinUrl}
              facebookUrl={facebookUrl}
              instagramUrl={instagramUrl}
              youtubeUrl={youtubeUrl}
              tiktokUrl={tiktokUrl}
              companyId={companyId}
              locale={jobLocale}
              accentColor={accentColor}
              className="gap-1.5"
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
